import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Filet de sécurité contre l'écran blanc.
 *
 * Une erreur de rendu non rattrapée démonte tout l'arbre React : l'utilisateur
 * se retrouve devant une page vide, sans explication ni moyen de repartir.
 *
 * C'est l'un des rares cas où un composant de classe reste nécessaire : React
 * n'expose pas encore `componentDidCatch` sous forme de hook.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Point de branchement d'un outil de supervision (Sentry, GlitchTip…).
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  private readonly handleReset = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    const { error } = this.state;
    const { children, fallback } = this.props;

    if (!error) return children;
    if (fallback) return fallback;

    return (
      <div className="flex min-h-dvh items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md rounded-lg border border-red-200 bg-white p-6 shadow-sm">
          <h1 className="text-lg font-semibold text-slate-900">Une erreur est survenue</h1>
          <p className="mt-2 text-sm text-slate-600">
            L&apos;affichage de cette page a échoué. Vous pouvez réessayer ; si le problème
            persiste, signalez-le à l&apos;équipe technique.
          </p>
          {import.meta.env.DEV ? (
            <pre className="mt-4 overflow-auto rounded bg-slate-100 p-3 text-xs text-red-700">
              {error.message}
            </pre>
          ) : null}
          <button
            type="button"
            onClick={this.handleReset}
            className="mt-4 rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }
}
